import { makeStyles } from '@material-ui/core';
import { CompetenceFilterParam, PublicUserDto, SkillDto, LevelOfSkillDto } from 'utils/wemble-api.generated';
import { useGetLevelOfSkillsQuery } from 'utils/wemble-api';
import SkillLevelDots from 'components/shared/SkillLevelDots';

const useStyles = makeStyles({
  container: {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: '5px 12px',
    marginTop: 8,
    userSelect: 'none',
    WebkitUserSelect: 'none',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 0',
    '&:not(:last-child)': {
      borderBottom: '1px solid #f2f2f2',
    },
  },
  name: {
    fontSize: 12,
    color: '#444',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginRight: 10,
    lineHeight: 1.3,
  },
});

interface CompetenceAnnotationProps {
  user: PublicUserDto;
  competenceFilters: CompetenceFilterParam[];
}

const CompetenceAnnotation = ({ user, competenceFilters }: CompetenceAnnotationProps) => {
  const classes = useStyles();
  const { data: levels } = useGetLevelOfSkillsQuery();

  if (!competenceFilters?.length || !user.skills?.length) return null;

  const sortedLevels = levels ? [...levels].filter(l => l.level != null).sort((a, b) => (a.level ?? 0) - (b.level ?? 0)) : [];
  const levelCount = sortedLevels.length;

  // Scale dots based on number of levels
  const dotSize = levelCount > 10 ? 8 : levelCount > 6 ? 10 : 12;
  const dotGap = levelCount > 10 ? 2 : 3;

  const rows = competenceFilters.map(filter => {
    const skill = (user.skills as SkillDto[])?.find(s => {
      const comp = s.competence;
      const compId = typeof comp === 'string' ? comp : comp?._id;
      return compId === filter.competenceId;
    });

    if (!skill) return null;

    const comp = skill.competence;
    const competenceName = typeof comp === 'object' ? (comp as any)?.name : null;
    const lvl = skill.level;
    const userLevel = typeof lvl === 'object' ? (lvl as LevelOfSkillDto)?.level ?? -1 : -1;

    return { competenceId: filter.competenceId, competenceName, userLevel };
  }).filter(Boolean) as { competenceId: string; competenceName: string | null; userLevel: number }[];

  if (rows.length === 0) return null;

  return (
    <div className={classes.container}>
      {rows.map(row => (
        <div key={row.competenceId} className={classes.row}>
          <span className={classes.name}>{row.competenceName || '—'}</span>
          <SkillLevelDots
            levels={sortedLevels}
            userLevel={row.userLevel}
            dotSize={dotSize}
            dotGap={dotGap}
          />
        </div>
      ))}
    </div>
  );
};

export default CompetenceAnnotation;
